import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, MapPin } from "lucide-react";
import { api, inr } from "../lib/api";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

export default function OrderSuccess() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => setNotFound(true));
  }, [id]);

  return (
    <div data-testid="order-success-page" className="min-h-screen flex flex-col">
      <Header />
      <main className="max-w-3xl mx-auto px-6 py-16 w-full flex-1">
        {notFound && (
          <div data-testid="order-not-found" className="bg-white border border-border rounded-md p-12 text-center">
            <p className="text-muted-foreground">Order not found.</p>
            <Link to="/" className="text-primary font-medium text-sm mt-2 inline-block hover:underline">Back to shop</Link>
          </div>
        )}
        {order && (
          <div className="bg-white border border-border rounded-md p-8 sm:p-12 fade-up">
            <div className="flex items-start gap-4">
              <CheckCircle2 className="w-10 h-10 text-secondary shrink-0" strokeWidth={2} />
              <div>
                <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Order placed successfully!</h1>
                <p className="text-muted-foreground text-sm mt-1">
                  Thank you, {order.customer.name}. Our team will call {order.customer.phone} shortly to confirm delivery and installation.
                </p>
              </div>
            </div>
            <div className="mt-8 border border-border rounded-md p-6 bg-muted/40">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-1">Order ID</p>
              <p data-testid="order-id" className="font-mono text-sm break-all">{order.id}</p>
            </div>
            <div className="mt-6 space-y-3">
              {order.items.map((item) => (
                <div key={item.product_id} data-testid={`order-item-${item.product_id}`} className="flex justify-between text-sm">
                  <span>{item.name} &times; {item.qty}</span>
                  <span className="font-medium">{inr(item.line_total)}</span>
                </div>
              ))}
              <div className="border-t border-border pt-3 flex justify-between font-heading font-bold text-lg">
                <span>{order.payment?.status === "paid" ? "Total (Paid Online)" : "Total (Pay on Delivery)"}</span>
                <span data-testid="order-total" className="text-primary">{inr(order.total)}</span>
              </div>
              {order.payment?.status === "paid" && (
                <p data-testid="payment-paid-badge" className="mt-3 inline-flex items-center gap-2 rounded-full bg-green-100 text-green-800 text-xs font-semibold px-4 py-1.5">
                  Payment received via Razorpay &middot; ID {order.payment.razorpay_payment_id}
                </p>
              )}
            </div>
            <div className="mt-8 flex items-start gap-2 text-sm text-muted-foreground">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
              <span>Jehovah Nissi Solar Agency, New No 15/1, Khajapetta, Thiruchappali, Tamil Nadu 620001 &middot; <a href="tel:+916374135100" data-testid="success-phone-link" className="text-primary font-medium hover:underline">+91 63741 35100</a></span>
            </div>
            <Link to="/" data-testid="continue-shopping-link" className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary text-white font-medium px-6 py-3 hover:bg-primary/90 active:scale-95 transition-[background-color,transform] duration-200">
              Continue Shopping
            </Link>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
